import { PhotoTrigger } from '../components/photo-viewer.tsx';
import '../components/work-page.css';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { magicQuoteApi } from '../api/endpoints.ts';
import { ApiRequestError } from '../api/client.ts';
import { Alert, Button, ConfirmDialog, LoadingBlock, TextArea } from '../components/ui.tsx';
import { useAiDisclosure } from '../components/ai-disclosure.tsx';
import { useSession } from '../state/session.tsx';
import { useDocumentTitle, useOnlineStatus, useUnsavedChangesWarning } from '../hooks/index.ts';
import { MAX_AUDIO_SECONDS, MAX_VIDEO_SECONDS, useWalkthroughCapture } from '../hooks/use-walkthrough-capture.ts';
import CaptureTimeline from '../components/magic-quote/capture-timeline.tsx';
import AddressLink from '../components/address-link.tsx';
import { pendingFor } from '../state/capture-queue.ts';
import { findLocalWalkthrough } from '../state/local-walkthrough.ts';
import { isLocalWalkthroughId, resolveWalkthroughId } from '../state/walkthrough-id.ts';
import type { MagicQuoteCapture } from '@shared/types/index.ts';

/**
 * The walkthrough as this screen needs it, from wherever it could be had.
 *
 * `known` is the honest bit: false means the server was not reachable and the
 * name, the address and the timeline are absent rather than empty. The
 * difference matters on screen — "No customer yet" is a claim about the
 * walkthrough, and saying it about one whose customer simply could not be
 * fetched is a lie the estimator would act on.
 */
interface WalkthroughView {
  customerName: string | null;
  propertyAddress: string | null;
  /**
   * Whether that address is an address, rather than the label a local record
   * holds. `startLocalWalkthrough` stores the property's nickname, or its
   * street line on its own — enough to recognize a row by in a driveway, and
   * not enough to point a map at: "Warehouse" is not a place, and a street
   * with no town exists in hundreds of them.
   */
  propertyAddressMappable: boolean;
  captures: MagicQuoteCapture[];
  pendingCaptureCount: number;
  speechRecognitionAvailable: boolean;
  known: boolean;
}

/**
 * The walkthrough screen: what a cleaner looks at while standing in somebody's
 * kitchen with the customer beside them.
 *
 * Every decision here answers to one metric — how long the cleaner spends
 * looking at this instead of at the house. So: four enormous targets, no
 * form, nothing to categorize, no confirmation dialogs, and no state that can
 * make a capture fail. Photos take one tap and return straight to the camera,
 * because a walkthrough is usually four photos of a kitchen and not one.
 *
 * The timeline underneath is deliberately secondary. It exists so the cleaner
 * can glance down and see that the thing they just did was recorded — and so
 * they can look back later — not as something to work in during the visit.
 */
export default function MagicQuoteWalkthroughPage() {
  const { t } = useTranslation();
  const { sessionId = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { company, can, currentLocation } = useSession();
  useDocumentTitle(t('magicQuote.walkthrough.documentTitle'));

  // A company that switched video or audio off will refuse those uploads, and
  // the refusal arrives long after the cleaner pressed the button and walked
  // into the next room. So the button is not offered: the only honest place to
  // enforce a setting the estimator cannot change is before the recording
  // exists, not after it is sitting on their phone with nowhere to go.
  const settings = company?.aiSettings?.magicQuote;
  const allowVideo = settings?.video !== false;
  const allowAudio = settings?.audio !== false;

  // What the shutter does that a cleaner can actually perceive. A photo that
  // produces no flash, no sound and no visible change is one people take twice
  // because they cannot tell whether the first one happened.
  const [flash, setFlash] = useState(false);
  const [shotCount, setShotCount] = useState(0);

  const [noteDraft, setNoteDraft] = useState('');
  const [roomDraft, setRoomDraft] = useState('');
  const [panel, setPanel] = useState<'none' | 'note' | 'room'>('none');
  const [error, setError] = useState<string | null>(null);
  /** A shot that did not happen, said where the shutter is rather than behind it. */
  const [shutterError, setShutterError] = useState<string | null>(null);
  /** How far through a library selection the phone has got, while it is reading one. */
  const [adding, setAdding] = useState<{ done: number; total: number } | null>(null);
  /**
   * The file picker behind the library button.
   *
   * A real `<input type="file">` rather than anything clever: it is what opens
   * the phone's own photo picker, with its own multi-select, its own search
   * and its own permissions — none of which a web page can imitate and all of
   * which the cleaner already knows how to use.
   */
  const libraryInput = useRef<HTMLInputElement>(null);

  /**
   * A walkthrough that exists on this device and nowhere else yet — started
   * with no signal, and waiting for one to be handed over on.
   *
   * Everything the server would have said about it is unknown by definition,
   * so the screen is drawn from the record the device kept instead. Capture
   * itself is unaffected: it was never talking to the server anyway.
   */
  const isLocal = isLocalWalkthroughId(sessionId);
  const online = useOnlineStatus();

  const scope = { companyId: company?.id ?? null, locationId: currentLocation?.id ?? null };
  const localRecord = useQuery({
    queryKey: ['magic-quote', 'local', sessionId, scope.companyId, scope.locationId],
    // Scoped to the company, and behind the permission that lets somebody
    // capture at all. The id is in the URL, so this screen is reachable by
    // typing it — including by a colleague at another company who signed in on
    // the same browser, who must not be handed the customer's name or the
    // photographs queued underneath it.
    queryFn: () => (can('quotes:write') ? findLocalWalkthrough(sessionId, scope) : null),
    enabled: isLocal,
    // IndexedDB, not the network. Left to pause on `navigator.onLine` this
    // would never resolve in the one case it is for.
    networkMode: 'always',
    refetchInterval: 5_000,
  });

  const session = useQuery({
    queryKey: ['magic-quote', sessionId],
    queryFn: () => magicQuoteApi.get(sessionId),
    enabled: Boolean(sessionId) && !isLocal,
    // The pipeline fills things in behind the walkthrough; a slow poll keeps
    // the timeline honest without the cleaner ever pulling to refresh.
    refetchInterval: 15_000,
  });

  /**
   * Follows a walkthrough that was handed over while this screen was open.
   *
   * The estimator was taking photos; nothing about the moment their phone
   * found signal is theirs to notice or act on. Replaced rather than pushed,
   * so Back still leads where it did before the id changed underneath them.
   */
  const adoptedAs = isLocal ? (localRecord.data?.serverId ?? null) : null;
  useEffect(() => {
    if (!adoptedAs) return;
    // Flushed before the redirect, not after it. Most captures are in
    // IndexedDB and would be drained by the queue whoever is looking at them —
    // but the ones the store REFUSED live in this hook's memory and nowhere
    // else, and navigating unmounts the only thing holding them. The hook's
    // own timer is eight seconds and this redirect fires within five, so
    // leaving it to chance means losing them roughly whenever the two race.
    let left = false;
    void capture.sync().finally(() => {
      // Only once nothing is held. `sync` stops at the first failure and
      // resolves normally, so navigating on completion rather than on success
      // would unmount the hook with a capture still in memory — its only copy
      // — on exactly the weak connection this path exists for. Staying costs
      // nothing: the queue resolves the adopted id either way, so the screen
      // works perfectly well under the old URL until the next attempt clears
      // it.
      if (!left && heldCaptures.current === 0) {
        navigate(`/magic-quote/${adoptedAs}`, { replace: true });
      }
    });
    return () => {
      left = true;
    };
    // `capture` is rebuilt on most renders; depending on it would restart this
    // effect — and its sync — on every one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adoptedAs, navigate]);

  /**
   * Captures with nowhere durable to live, as of the last sync.
   *
   * Read through a ref because the redirect effect must not re-run every time
   * the count changes — it would restart its own sync.
   */
  const heldCaptures = useRef(0);
  const disclosure = useAiDisclosure();

  const capture = useWalkthroughCapture(sessionId, {
    beforeSync: async () => {
      // A walkthrough that still lives only on the phone has nothing to send.
      // A resumed server walkthrough must show the notice before uploads,
      // notes, and completion — those posts are refused until it is accepted,
      // and the queue would otherwise retry the 403 forever.
      if (isLocalWalkthroughId(resolveWalkthroughId(sessionId))) return;
      await disclosure.ensure();
    },
    onCaptured: () => {
      void queryClient.invalidateQueries({ queryKey: ['magic-quote', sessionId] });
      // And the list behind this screen, which counts what is on the device.
      // Without this a cleaner who adds six photos and presses Back is shown
      // the count from before they started — and, worse, is asked to confirm
      // deleting "0 captures" for a walkthrough holding six.
      if (isLocal) void queryClient.invalidateQueries({ queryKey: ['magic-quotes', 'local'] });
    },
    allowAudio,
  });

  heldCaptures.current = capture.status.held;

  /**
   * Words typed into the note or room box and not yet saved with its button.
   *
   * Those buttons are small and secondary, and Finish is the big one in the
   * bar directly underneath, so a cleaner who types a sentence about the
   * grout and reaches for Finish has said what they meant — losing it to the
   * wrong button is not an answer, and neither is a dialog in somebody's
   * kitchen. Finishing, leaving the screen and starting the next capture all
   * save it as its own button would have. Read through a ref so the save on the way out sees the text
   * as it was last typed.
   */
  const drafts = useRef({ note: '', room: '' });
  drafts.current = { note: noteDraft, room: roomDraft };
  /**
   * When each box was started, as a running count. Switching panels keeps the
   * other box's text, so both can be full at once — and a room marker files
   * everything after it, so they are saved in the order they were written: a
   * note about the kitchen typed before "Bathroom" stays in the kitchen.
   */
  const draftStarted = useRef({ note: 0, room: 0, next: 1 });
  const editDraft = (kind: 'note' | 'room', value: string) => {
    const started = draftStarted.current;
    if (!value.trim()) started[kind] = 0;
    else if (!started[kind]) started[kind] = started.next++;
    (kind === 'note' ? setNoteDraft : setRoomDraft)(value);
  };
  /** The save under way, so finishing and leaving cannot both send one sentence. */
  const savingDrafts = useRef<Promise<void> | null>(null);
  const saveDrafts = () => {
    savingDrafts.current ??= (async () => {
      const text = { note: drafts.current.note.trim(), room: drafts.current.room.trim() };
      const kinds = (['note', 'room'] as const)
        .filter((kind) => text[kind])
        .sort((a, b) => draftStarted.current[a] - draftStarted.current[b]);
      for (const kind of kinds) {
        if (kind === 'room') await capture.markRoom(text.room);
        else await capture.addNote(text.note);
      }
      if (kinds.length === 0) return;
      // Cleared only once both are in the queue: until then the boxes are the
      // only copy, and the unload warning has to stay on for them.
      drafts.current = { note: '', room: '' };
      editDraft('note', '');
      editDraft('room', '');
      setPanel('none');
    })().finally(() => {
      savingDrafts.current = null;
    });
    return savingDrafts.current;
  };
  const saveDraftsOnLeave = useRef(saveDrafts);
  saveDraftsOnLeave.current = saveDrafts;
  useEffect(() => () => void saveDraftsOnLeave.current(), []);
  // A reload or a closed tab runs no cleanup at all, so that one is asked.
  useUnsavedChangesWarning(Boolean(noteDraft.trim() || roomDraft.trim()));

  /** When Finish was pressed, so the wait can be counted down rather than guessed at. */
  const [finishingSince, setFinishingSince] = useState<number | null>(null);

  /** Asked before finishing with captures that never reached the server. */
  const [finishAnyway, setFinishAnyway] = useState(false);

  const finish = useMutation({
    onMutate: () => setFinishingSince(Date.now()),
    // `leaveBehind` is the way past an upload that will not go through: the
    // estimator has been told those captures stay out, and chose to finish.
    mutationFn: async ({ leaveBehind }: { leaveBehind: boolean }) => {
      // Everything held locally has to reach the server before the
      // walkthrough is reconciled — leaving this screen unmounts the only
      // thing that flushes the queue, and a completed session reopens
      // straight into review, so a capture still on the phone at this moment
      // would never be uploaded at all. That includes a note still sitting in
      // its box, and anything the local store refused: those live only in
      // the hook's memory, which `pendingFor` cannot see.
      await saveDrafts();
      const held = await capture.sync();
      const remaining = await pendingFor(sessionId);
      if (!leaveBehind && (held > 0 || remaining.some((item) => !item.abandoned))) {
        throw new Error(t('magicQuote.walkthrough.stillUploading', { count: held + remaining.length }));
      }
      return magicQuoteApi.complete(sessionId);
    },
    onSuccess: (result) => {
      setFinishAnyway(false);
      if (!result.ok && result.reason) setError(result.reason);
      navigate(`/magic-quote/${sessionId}/review`);
    },
    onError: (cause: Error) => {
      setFinishingSince(null);
      setFinishAnyway(false);
      setError(cause.message);
    },
  });

  // Anything still in the local queue has to reach the server before the
  // walkthrough is reconciled, or it is reconciled without it.
  const unsynced = capture.status.pending;

  const [startedAt] = useState(() => Date.now());
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  if (isLocal ? localRecord.isLoading : session.isLoading) {
    return <LoadingBlock label={t('magicQuote.walkthrough.loading')} />;
  }

  /**
   * What the screen draws from, whether or not the server could be asked.
   *
   * Three sources, in the order they are trusted: the walkthrough the server
   * returned, the record this device kept for one it started offline, and —
   * for a walkthrough that IS on the server, being resumed with no signal —
   * nothing at all, which is a perfectly workable state and used to be an
   * error screen.
   *
   * That last case is the one worth being explicit about. Resuming offline
   * cannot show the timeline, the customer's name or what has been read so far,
   * because all three come from a request that cannot be made. It can do the
   * only thing that matters in the house: keep capturing, against the id the
   * queue already files under, for upload when there is signal. Refusing the
   * whole screen because the decoration is missing threw away the feature to
   * protect the furniture.
   */
  const view: WalkthroughView = session.data
    ? {
        customerName: session.data.customerName,
        propertyAddress: session.data.propertyAddress,
        propertyAddressMappable: true,
        captures: session.data.captures,
        pendingCaptureCount: session.data.pendingCaptureCount,
        speechRecognitionAvailable: session.data.speechRecognitionAvailable,
        known: true,
      }
    : {
        customerName: localRecord.data?.customerName ?? null,
        propertyAddress: localRecord.data?.propertyAddress ?? null,
        propertyAddressMappable: false,
        // Empty rather than absent: what is on the phone is shown by the strip
        // under the viewfinder and counted by the sync badge, and inventing a
        // timeline out of the queue would claim the server has read things it
        // has never been sent.
        captures: [],
        pendingCaptureCount: 0,
        speechRecognitionAvailable: false,
        known: isLocal,
      };

  /**
   * Whether the failure was the network rather than an answer.
   *
   * `navigator.onLine` is not enough on its own: one bar in a basement reports
   * online and carries nothing, and a request that dies in the air throws a
   * `TypeError` from `fetch` rather than an `ApiRequestError` — no response
   * ever came back to have a status. Both of those are "no signal", and both
   * should leave the camera on screen. A real 404 or 403 is an answer about
   * this walkthrough, and that still gets the error.
   */
  const unreachable = !online || (session.isError && !(session.error instanceof ApiRequestError));

  // Nothing to draw and no way to get it: a walkthrough this device has no
  // record of, or a server one the server actually answered about.
  if (isLocal ? !localRecord.data : session.isError && !unreachable) {
    return (
      <Alert tone="error" title={t('magicQuote.walkthrough.loadErrorTitle')}>
        {isLocal ? t('magicQuote.walkthrough.localMissingBody') : t('magicQuote.walkthrough.loadErrorBody')}
      </Alert>
    );
  }

  const data = view;
  const elapsedLabel = formatClock(Math.floor((now - startedAt) / 1000));
  /**
   * The strip under the viewfinder: this session's own shots first.
   *
   * The local previews come from the blob the camera just produced, so they
   * appear the instant the shutter fires and keep working with no signal. The
   * server's copies are the fallback — a walkthrough resumed on a second visit
   * has photos worth showing that this page never took.
   */
  const serverPhotos = [...data.captures].reverse().filter((entry) => entry.type === 'photo' && entry.mediaUrl);
  // Which of this session's shots the server has since taken delivery of.
  const landed = new Set(serverPhotos.map((entry) => entry.clientCaptureId).filter(Boolean));
  const recentShots = [
    // This session's own shots first — they exist the instant the shutter
    // fires — then the walkthrough's earlier ones behind them. Choosing one
    // source or the other emptied the strip of five resumed photos the moment
    // a sixth was taken, which is the opposite of what a coverage check is
    // for: a cleaner picking up yesterday's walkthrough lost the record of it
    // by adding to it.
    //
    // A local preview retires the moment its uploaded copy appears, or the
    // strip draws the same photo twice: three shots online would fill all six
    // slots with duplicates and hide the rest of the walk, which is the same
    // coverage check failing by the opposite route.
    ...capture.recentPhotos.filter((photo) => !landed.has(photo.clientCaptureId)).map((photo) => photo.url),
    ...serverPhotos.map((entry) => entry.mediaUrl as string),
  ].slice(0, 6);

  const submitNote = async () => {
    const text = noteDraft.trim();
    if (!text) return;
    editDraft('note', '');
    setPanel('none');
    await capture.addNote(text);
  };

  /**
   * Take one photo, and say so on the way.
   *
   * The flash fires before the frame is grabbed rather than after it lands:
   * the feedback is for the press, and a confirmation that waits for a canvas
   * encode reads as lag. The count beside it is the durable half — a flash is
   * gone in a blink, and "4 photos" is still there when the cleaner looks
   * down a second later.
   */
  const shoot = async () => {
    setFlash(true);
    window.setTimeout(() => setFlash(false), 140);
    // Phones that support it: the same short buzz a camera app gives. Guarded
    // because iOS Safari does not implement it, and a missing method here
    // would throw inside the shutter handler.
    navigator.vibrate?.(30);

    // Counted only when there is something to count. The shutter can be
    // pressed before the first frame arrives, and telling somebody a photo
    // was taken when none was is worse than the flash they already saw —
    // they leave the room on the strength of it.
    if (await capture.takePhoto()) {
      setShotCount((count) => count + 1);
      setShutterError(null);
    } else {
      // Said ON the viewfinder, not in the page's alert strip. The viewfinder
      // is a fixed overlay above the whole page, so an alert behind it is an
      // explanation nobody can see: the cleaner gets the flash and the buzz,
      // no photo, and — unless they happen to notice the count did not move —
      // walks out of the room believing they have one.
      setShutterError(t('magicQuote.walkthrough.shutterNotReady'));
    }
  };

  /**
   * Photos chosen from the phone's library.
   *
   * The same flash the shutter gives, because the same thing happened: photos
   * are now in the walkthrough. The count moves by however many actually
   * landed — a selection can be part refused, and saying "6 photos" over four
   * is the failure the shutter already had to be fixed for.
   */
  const pickPhotos = async (list: FileList | null) => {
    const files = [...(list ?? [])];
    if (files.length === 0) return;

    setFlash(true);
    window.setTimeout(() => setFlash(false), 140);
    navigator.vibrate?.(30);

    // Fifteen photos take a phone real seconds to read, and a screen that
    // shows nothing while it works is a screen somebody presses again.
    const { added, skipped } = await capture.addPhotos(files, (progress) =>
      setAdding(progress.done < progress.total ? progress : null),
    );
    setAdding(null);
    setShotCount((count) => count + added);
    setShutterError(skipped > 0 ? t('magicQuote.walkthrough.someNotImages', { count: skipped }) : null);
  };

  const submitRoom = async () => {
    const name = roomDraft.trim();
    if (!name) return;
    editDraft('room', '');
    setPanel('none');
    await capture.markRoom(name);
  };

  return (
    <div className="work-page mx-auto flex max-w-2xl flex-col gap-4 pb-24">
      {disclosure.dialog}
      {disclosure.declined ? (
        <Alert tone="warning">
          {t('aiDisclosure.declinedHint')}{' '}
          <button type="button" className="font-semibold underline" onClick={() => void disclosure.ensure(true)}>
            {t('aiDisclosure.review')}
          </button>
        </Alert>
      ) : null}
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold break-words">
            {data.customerName ??
              t(data.known ? 'magicQuote.walkthrough.noCustomer' : 'magicQuote.walkthrough.offlineCustomer')}
          </h1>
          {data.propertyAddress ? (
            <p className="text-sm whitespace-pre-line text-[color:var(--color-muted)]">
              {data.propertyAddressMappable ? (
                <AddressLink address={data.propertyAddress}>{data.propertyAddress}</AddressLink>
              ) : (
                data.propertyAddress
              )}
            </p>
          ) : null}
        </div>
        {/* The clock is a chip rather than loose gray text: it is the one
            number on this screen that is always moving, and it should read as
            an instrument rather than as a caption. */}
        <p
          className="tabular shrink-0 rounded-full bg-[color:var(--color-surface)] px-3 py-1 text-sm font-semibold text-[color:var(--color-muted)]"
          aria-live="off"
        >
          {elapsedLabel}
        </p>
      </header>

      {/* Forced, because this is a person asking. The plain sync respects the
          backoff a failed capture is sitting out, which made Retry do nothing
          at all for up to a minute — the button somebody presses precisely
          because nothing is happening. */}
      <SyncBadge status={capture.status} onRetry={() => void capture.sync({ force: true })} />

      {/* Said once, at the top, rather than left for the estimator to infer
          from an empty timeline. Both halves matter: keep walking, and the
          walkthrough is not organized until it has been sent. */}
      {isLocal ? (
        <Alert tone="info" title={t('magicQuote.walkthrough.localTitle')}>
          {t('magicQuote.walkthrough.localBody')}
        </Alert>
      ) : !data.known ? (
        <Alert tone="info" title={t('magicQuote.walkthrough.offlineTitle')}>
          {t('magicQuote.walkthrough.offlineBody')}
        </Alert>
      ) : null}

      {error ? (
        <Alert tone="error" title={t('magicQuote.walkthrough.actionErrorTitle')}>
          {error}
        </Alert>
      ) : null}

      {capture.deviceError ? (
        <Alert tone="warning" title={t('magicQuote.walkthrough.deviceErrorTitle')}>
          {capture.deviceError}
        </Alert>
      ) : null}

      {/* The viewfinder takes the whole screen. It used to sit in the page
          flow above the buttons and the timeline, which left a cleaner
          framing a shower through a postcard while two thirds of the display
          showed a list they were not reading. What is being recorded is the
          only thing that matters while recording, so it gets the screen. */}
      {capture.mode === 'camera' || capture.mode === 'video' || capture.mode === 'saving' ? (
        <div className="fixed inset-0 z-50 bg-black">
          {/* muted is required for autoplay to be allowed at all; playsInline
              stops iOS taking the preview fullscreen on its own terms, with
              its own controls, mid-walkthrough. */}
          <video ref={capture.videoRef} className="h-full w-full object-cover" muted playsInline />

          {/* Scrims, not a tinted overlay: the controls need a dark ground to
              read against, and a white kitchen ceiling is exactly the scene
              that would otherwise swallow them — but the middle of the frame,
              which is what is being photographed, stays untouched. */}
          <div
            className="pointer-events-none absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/60 to-transparent"
            aria-hidden="true"
          />
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0 h-56 bg-gradient-to-t from-black/70 to-transparent"
            aria-hidden="true"
          />

          {/* Flash on top of everything, including the video. */}
          {flash ? <div className="pointer-events-none absolute inset-0 bg-white" aria-hidden="true" /> : null}

          {/* No `capture` attribute on purpose: that one would force the
              camera and this button is the other half of the pair. Multiple,
              because somebody adding photos of a job has more than one. */}
          <input
            ref={libraryInput}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(event) => {
              void pickPhotos(event.target.files);
              // Cleared so choosing the same photo twice still fires a change.
              event.target.value = '';
            }}
          />

          <div className="absolute inset-x-0 top-0 flex items-center justify-between gap-3 p-4 pt-[max(1rem,env(safe-area-inset-top))] text-white">
            <p className="text-sm font-medium drop-shadow">
              {capture.mode === 'saving' ? (
                t('magicQuote.walkthrough.saving')
              ) : capture.mode === 'video' ? (
                <>
                  <span className="mr-2 inline-block h-3 w-3 rounded-full bg-red-500 align-middle" aria-hidden="true" />
                  {/* Counted DOWN, not up. A clip stops itself at a minute,
                      and a number climbing from zero gives no warning that it
                      is about to — the recorder simply stopped mid-sentence,
                      which reads as the app breaking rather than as a limit
                      being reached. */}
                  {t('magicQuote.walkthrough.recordingLeft', {
                    left: formatClock(Math.max(0, MAX_VIDEO_SECONDS - capture.elapsed)),
                  })}
                </>
              ) : shotCount > 0 ? (
                t('magicQuote.walkthrough.shotCount', { count: shotCount })
              ) : (
                t('magicQuote.walkthrough.framePrompt')
              )}
            </p>
            {capture.mode === 'camera' ? (
              <button
                type="button"
                className="rounded-full bg-white/15 px-5 py-2 text-sm font-semibold text-white ring-1 ring-white/40 backdrop-blur transition-colors hover:bg-white/25"
                onClick={() => {
                  setShotCount(0);
                  setShutterError(null);
                  capture.close();
                }}
              >
                {t('common.done')}
              </button>
            ) : null}
          </div>

          {/* The last few captures, along the bottom edge of the viewfinder.
              The timeline is behind the fullscreen preview now, so without
              this the running count is the only evidence a photo exists —
              and a count cannot show that the last one was of the floor. */}
          {capture.mode !== 'saving' && recentShots.length > 0 ? (
            <div className="absolute inset-x-0 bottom-32 flex gap-2 overflow-x-auto px-4 pb-2">
              {recentShots.map((url, index) => (
                <PhotoTrigger
                  key={url}
                  src={url}
                  contentType={url.startsWith('blob:') ? 'image/jpeg' : undefined}
                  alt={t('quickQuote.photos.alt', { number: index + 1 })}
                  group="walkthrough-recent"
                  className="shrink-0"
                >
                  <img
                    src={url}
                    alt={t('quickQuote.photos.alt', { number: index + 1 })}
                    className="h-14 w-14 shrink-0 rounded-xl object-cover shadow-lg ring-2 ring-white/70"
                  />
                </PhotoTrigger>
              ))}
            </div>
          ) : null}

          <div className="absolute inset-x-0 bottom-0 p-6 pb-[max(1.5rem,env(safe-area-inset-bottom))]">
            {adding ? (
              <p
                className="mx-auto mb-4 max-w-sm rounded-xl bg-black/70 px-4 py-2 text-center text-sm font-semibold text-white shadow-lg"
                role="status"
              >
                {t('magicQuote.walkthrough.addingPhotos', { done: adding.done + 1, total: adding.total })}
              </p>
            ) : null}
            {shutterError && capture.mode === 'camera' ? (
              <p
                className="mx-auto mb-4 max-w-sm rounded-xl bg-[color:var(--color-danger)] px-4 py-2 text-center text-sm font-semibold text-white shadow-lg"
                role="alert"
              >
                {shutterError}
              </p>
            ) : null}
            {capture.mode === 'saving' ? (
              <p className="text-center text-lg font-semibold text-white" role="status">
                {t('magicQuote.walkthrough.saving')}
              </p>
            ) : capture.mode === 'camera' ? (
              // The shutter dead center with the library to its left: the
              // layout of every camera app a thumb has ever used, which is why
              // neither control needs a label to be found. One button opened
              // this screen; both ways of getting a photo into the walkthrough
              // are one tap from here.
              <div className="grid grid-cols-3 items-center">
                <div className="flex justify-start">
                  <button
                    type="button"
                    aria-label={t('magicQuote.walkthrough.fromLibrary')}
                    title={t('magicQuote.walkthrough.fromLibrary')}
                    className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15 text-white ring-1 ring-white/40 backdrop-blur transition-colors active:bg-white/30"
                    onClick={() => libraryInput.current?.click()}
                  >
                    <LibraryIcon />
                  </button>
                </div>

                {/* A round shutter, thumb-sized, in the middle where a thumb
                    already is — not a rectangle in a row of rectangles. */}
                {/* The camera-app shutter, because that is the control every
                    phone has trained a thumb to look for: a white ring with a
                    filled disc inside it, dead center, that visibly presses. */}
                <div className="flex justify-center">
                  <button
                    type="button"
                    aria-label={t('magicQuote.walkthrough.shutter')}
                    className="flex h-20 w-20 items-center justify-center rounded-full ring-4 ring-white transition-transform active:scale-90"
                    onClick={() => void shoot()}
                  >
                    <span className="h-16 w-16 rounded-full bg-white" aria-hidden="true" />
                  </button>
                </div>
                <div />
              </div>
            ) : (
              // Stop is a filled red pill on black rather than the app's
              // outlined danger button, which is drawn for a white page and
              // nearly disappears over a video frame.
              <button
                type="button"
                onClick={capture.stopRecording}
                className="tabular flex min-h-16 w-full items-center justify-center gap-3 rounded-full bg-red-600 text-lg font-semibold text-white transition-colors hover:bg-red-700"
              >
                <span className="h-5 w-5 rounded-[4px] bg-white" aria-hidden="true" />
                {t('magicQuote.walkthrough.stop', {
                  elapsed:
                    capture.mode === 'video'
                      ? formatClock(Math.max(0, MAX_VIDEO_SECONDS - capture.elapsed))
                      : formatClock(capture.elapsed),
                })}
              </button>
            )}
          </div>
        </div>
      ) : null}

      {/* Why it stopped, when it stopped itself. Otherwise a clip that ran to
          its limit and one somebody ended are the same event on screen, and a
          cleaner cut off mid-sentence reads it as the app failing. */}
      {capture.stoppedAtLimit && capture.mode === 'idle' ? (
        <Alert tone="info" title={t('magicQuote.walkthrough.limitTitle')}>
          {t('magicQuote.walkthrough.limitBody', {
            // The limit that was actually reached. A voice note runs to five
            // minutes and a clip to one, so naming the wrong one would be a
            // false explanation rather than a missing one.
            seconds: capture.stoppedAtLimit === 'audio' ? MAX_AUDIO_SECONDS : MAX_VIDEO_SECONDS,
          })}
        </Alert>
      ) : null}

      {capture.mode === 'audio' ? (
        <div className="rounded-2xl bg-[color:var(--color-ink)] p-6 text-center text-white">
          {/* A pulsing dot beside the clock, because an audio recording has no
              picture to prove it is running and a stopped one looks the same. */}
          <p className="flex items-center justify-center gap-2.5 text-3xl font-semibold tabular-nums">
            <span className="h-3 w-3 animate-pulse rounded-full bg-red-500" aria-hidden="true" />
            {formatClock(capture.elapsed)}
          </p>
          <p className="mt-1 text-sm text-white/70">
            {t('magicQuote.walkthrough.recordingHint', { seconds: MAX_AUDIO_SECONDS })}
          </p>
        </div>
      ) : null}

      <CaptureControls
        capture={capture}
        allowVideo={allowVideo}
        allowAudio={allowAudio}
        onNote={() => setPanel(panel === 'note' ? 'none' : 'note')}
        onRoom={() => setPanel(panel === 'room' ? 'none' : 'room')}
        // A room typed and not saved still names the room the next photo is
        // in, and the server files a capture under the last marker that
        // arrived before it — so it goes in the queue ahead of the capture,
        // not at Finish, behind everything taken since.
        onBeforeCapture={() => void saveDrafts()}
      />

      {panel === 'note' ? (
        <div className="flex flex-col gap-2">
          <TextArea
            label={t('magicQuote.walkthrough.noteLabel')}
            rows={3}
            value={noteDraft}
            onChange={(event) => editDraft('note', event.target.value)}
            placeholder={t('magicQuote.walkthrough.notePlaceholder')}
          />
          <Button onClick={() => void submitNote()} disabled={!noteDraft.trim()}>
            {t('magicQuote.walkthrough.saveNote')}
          </Button>
        </div>
      ) : null}

      {panel === 'room' ? (
        <div className="flex flex-col gap-2">
          <TextArea
            label={t('magicQuote.walkthrough.roomLabel')}
            rows={1}
            value={roomDraft}
            onChange={(event) => editDraft('room', event.target.value)}
            placeholder={t('magicQuote.walkthrough.roomPlaceholder')}
          />
          <Button onClick={() => void submitRoom()} disabled={!roomDraft.trim()}>
            {t('magicQuote.walkthrough.saveRoom')}
          </Button>
        </div>
      ) : null}

      <section>
        <h2 className="mb-2 text-sm font-semibold text-[color:var(--color-muted)] uppercase tracking-wide">
          {t('magicQuote.walkthrough.recent', { count: data.captures.length })}
        </h2>
        <CaptureTimeline
          sessionId={sessionId}
          captures={data.captures}
          speechRecognitionAvailable={data.speechRecognitionAvailable}
          compact
        />
      </section>

      {/* Finishing takes as long as it takes — every capture that has not been
          read yet is a model call, and the reconciliation on the end of them
          is another. A spinner on a button cannot carry a wait that long. */}
      {finishingSince !== null && finish.isPending ? (
        <FinishingOverlay
          startedAt={finishingSince}
          now={now}
          captureCount={data.captures.length}
          pendingCaptureCount={data.pendingCaptureCount}
        />
      ) : null}

      {/* Fixed rather than in flow: the way out of this screen should be in the
          same place whether the timeline holds two captures or forty. Above
          the phone's bottom navigation bar and beside the sidebar, where
          there is one (index.css). */}
      <div className="fixed right-0 bottom-[var(--shell-bottom-nav,0px)] left-[var(--shell-sidebar,0px)] border-t border-[color:var(--color-line)] bg-white/95 p-3 shadow-[0_-6px_20px_rgba(23,32,42,0.06)] backdrop-blur">
        <div className="mx-auto flex max-w-2xl items-center gap-3">
          <Button
            className="flex-1"
            variant="primary"
            onClick={() => finish.mutate({ leaveBehind: false })}
            // Held while anything is still on the phone: finishing would
            // reconcile the walkthrough without it.
            //
            // And held offline, which is not the same reason. Finishing is a
            // request, and a request made with no network does not fail — it
            // is *paused*, so the overlay would sit there organizing a
            // walkthrough nothing had been asked about, for as long as the
            // cleaner was willing to watch it. Refusing with a sentence
            // underneath is the honest version of the same answer.
            disabled={finish.isPending || capture.mode !== 'idle' || unsynced > 0 || unreachable || isLocal}
          >
            {finish.isPending ? t('magicQuote.walkthrough.finishing') : t('magicQuote.walkthrough.finish')}
          </Button>
          {unsynced > 0 ? (
            <span className="text-sm whitespace-nowrap text-[color:var(--color-warning)]">
              {t('magicQuote.sync.uploadingProgress', {
                done: capture.status.uploaded,
                total: capture.status.uploaded + unsynced,
              })}
            </span>
          ) : unreachable || isLocal ? (
            <span className="text-sm text-[color:var(--color-muted)]">{t('magicQuote.walkthrough.finishOffline')}</span>
          ) : null}
          {/* Only once the queue is stuck — it has said why it is not moving,
              or Finish has failed — never while it is simply uploading. And
              never offline: finishing is itself a request. */}
          {unsynced > 0 && (capture.status.lastError || error) && !unreachable && !isLocal ? (
            <Button onClick={() => setFinishAnyway(true)} disabled={finish.isPending || capture.mode !== 'idle'}>
              {t('magicQuote.walkthrough.finishAnyway')}
            </Button>
          ) : null}
        </div>
      </div>
      {finishAnyway ? (
        <ConfirmDialog
          title={t('magicQuote.walkthrough.finishAnywayTitle')}
          confirmLabel={t('magicQuote.walkthrough.finishAnyway')}
          busy={finish.isPending}
          onConfirm={() => finish.mutate({ leaveBehind: true })}
          onCancel={() => setFinishAnyway(false)}
        >
          <p>{t('magicQuote.walkthrough.finishAnywayBody', { count: unsynced })}</p>
        </ConfirmDialog>
      ) : null}
    </div>
  );
}

/**
 * The four capture buttons — the whole interface, really.
 *
 * Deliberately large and deliberately unlabeled-by-icon-alone: a cleaner
 * using this holds the phone in one hand and gestures at a shower with the
 * other, and every one of these has to be hittable without looking. The
 * shutter stays on screen after a photo so four photos of a kitchen are four
 * taps, not four round trips through a menu.
 */
function CaptureControls({
  capture,
  allowVideo,
  allowAudio,
  onNote,
  onRoom,
  onBeforeCapture,
}: {
  capture: ReturnType<typeof useWalkthroughCapture>;
  allowVideo: boolean;
  allowAudio: boolean;
  onNote: () => void;
  onRoom: () => void;
  onBeforeCapture: () => void;
}) {
  const { t } = useTranslation();

  // Camera and video are driven from the fullscreen viewfinder, which owns
  // the screen while either is open; only the voice note is controlled from
  // here, because it has nothing to look at.
  if (capture.mode === 'camera' || capture.mode === 'video' || capture.mode === 'saving') return null;

  if (capture.mode === 'audio') {
    return (
      <Button className="min-h-20 w-full text-lg" variant="danger" onClick={capture.stopRecording}>
        {t('magicQuote.walkthrough.stop', { elapsed: formatClock(capture.elapsed) })}
      </Button>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-3">
      {/* The camera leads: it is the thing a walkthrough is mostly made of,
          so it carries the brand fill and everything else steps back from it.
          Icons above labels rather than beside them — the tile is square, the
          thumb is aimed at its middle, and the word underneath is what makes
          the icon unambiguous rather than decorative. */}
      <CaptureTile
        tone="primary"
        label={t('magicQuote.walkthrough.camera')}
        icon={<CameraIcon />}
        onClick={() => {
          onBeforeCapture();
          void capture.openCamera();
        }}
      />
      {allowVideo ? (
        <CaptureTile
          label={t('magicQuote.walkthrough.video', { seconds: MAX_VIDEO_SECONDS })}
          icon={<VideoIcon />}
          onClick={() => {
            onBeforeCapture();
            void capture.startVideo();
          }}
        />
      ) : null}
      {allowAudio ? (
        <CaptureTile
          label={t('magicQuote.walkthrough.talk')}
          icon={<MicIcon />}
          onClick={() => {
            onBeforeCapture();
            void capture.startAudio();
          }}
        />
      ) : null}
      <div className="grid grid-rows-2 gap-3">
        <Button variant="secondary" className="h-full" onClick={onNote}>
          {t('magicQuote.walkthrough.note')}
        </Button>
        <Button variant="secondary" className="h-full" onClick={onRoom}>
          {t('magicQuote.walkthrough.markRoom')}
        </Button>
      </div>
    </div>
  );
}

/**
 * One of the big capture targets.
 *
 * Sized for a thumb that is not looking: 6rem tall, the icon well clear of the
 * label, and the whole tile is the target rather than the text inside it.
 */
function CaptureTile({
  label,
  icon,
  tone = 'secondary',
  onClick,
}: {
  label: string;
  icon: ReactNode;
  tone?: 'primary' | 'secondary';
  onClick: () => void;
}) {
  const primary = tone === 'primary';
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'flex min-h-24 flex-col items-center justify-center gap-1.5 rounded-xl border text-base font-semibold transition-colors',
        primary
          ? 'border-[color:var(--color-brand)] bg-[color:var(--color-brand)] text-white hover:bg-[color:var(--color-brand-strong)]'
          : 'border-[color:var(--color-line)] bg-white text-[color:var(--color-ink)] hover:bg-[color:var(--color-surface)]',
      ].join(' ')}
    >
      <span className={primary ? 'text-white' : 'text-[color:var(--color-brand)]'} aria-hidden="true">
        {icon}
      </span>
      {label}
    </button>
  );
}

/* Line icons at a common 24px box, stroked in `currentColor` so each one takes
   the color of the tile it sits in. */
function CameraIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M3 8.5A1.5 1.5 0 0 1 4.5 7h2.2l1.2-2h8.2l1.2 2h2.2A1.5 1.5 0 0 1 21 8.5v9A1.5 1.5 0 0 1 19.5 19h-15A1.5 1.5 0 0 1 3 17.5z" />
      <circle cx="12" cy="13" r="3.6" />
    </svg>
  );
}

function VideoIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <rect x="3" y="6.5" width="12.5" height="11" rx="2" />
      <path d="M15.5 11l5-2.6v7.2l-5-2.6z" />
    </svg>
  );
}

/**
 * Roughly how long finishing takes, so the wait can be counted down.
 *
 * `complete` is not one call. It reads every capture that has not been read
 * yet — one model round trip each, up to twelve in this pass — and then
 * reconciles the whole walkthrough, which is the long one. So the estimate is
 * per-capture work plus a constant, and it is deliberately a little generous:
 * a countdown that finishes early is a pleasant surprise, and one that sits on
 * zero is the stall it was meant to explain.
 */
const SECONDS_PER_UNREAD_CAPTURE = 7;
const SECONDS_TO_RECONCILE = 40;

/**
 * The screen while a walkthrough is being finished.
 *
 * Pressing Finish used to show a spinner on the button and then nothing for a
 * minute and a half, which is indistinguishable from a hang — long enough that
 * the honest reading is that the app has stopped. So the wait gets the whole
 * screen: what is happening, roughly how much longer, and the one reassurance
 * that matters, which is that the captures are already safely uploaded and
 * nothing here can lose them.
 *
 * The bar fills against the estimate and stops at 95%, because arriving at
 * "100%" while still working is how a progress bar loses its credibility.
 */
function FinishingOverlay({
  startedAt,
  now,
  captureCount,
  pendingCaptureCount,
}: {
  startedAt: number;
  now: number;
  captureCount: number;
  pendingCaptureCount: number;
}) {
  const { t } = useTranslation();
  const elapsed = Math.max(0, Math.floor((now - startedAt) / 1000));
  // Unread captures drive the estimate; a walkthrough whose captures were all
  // read while the cleaner was still walking only has the reconciliation left.
  const estimate = Math.max(20, pendingCaptureCount * SECONDS_PER_UNREAD_CAPTURE + SECONDS_TO_RECONCILE);
  const remaining = Math.max(0, estimate - elapsed);
  const percent = Math.min(95, Math.round((elapsed / estimate) * 100));

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[color:var(--color-ink)]/95 p-6"
      role="status"
      aria-live="polite"
    >
      <div className="w-full max-w-sm text-center text-white">
        <p className="text-xl font-semibold">{t('magicQuote.walkthrough.finishingTitle')}</p>
        <p className="mt-2 text-sm text-white/70">
          {t('magicQuote.walkthrough.finishingBody', { count: captureCount })}
        </p>

        <div className="mt-5 h-2 overflow-hidden rounded-full bg-white/20">
          <div
            className="h-full rounded-full bg-white transition-[width] duration-1000 ease-linear"
            style={{ width: `${Math.max(4, percent)}%` }}
          />
        </div>

        <p className="tabular mt-3 text-sm font-medium">
          {remaining > 0
            ? t('magicQuote.walkthrough.finishingRemaining', { time: formatClock(remaining) })
            : t('magicQuote.walkthrough.finishingNearlyDone')}
        </p>
        <p className="mt-4 text-xs text-white/60">{t('magicQuote.walkthrough.finishingSafe')}</p>
      </div>
    </div>
  );
}

/** Stacked photographs — the picture-library glyph a phone already uses. */
function LibraryIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <rect x="7.5" y="3.5" width="13" height="13" rx="2.5" />
      <circle cx="11.5" cy="7.5" r="1.4" />
      <path d="M9 15l3.2-3.6 2.4 2.6 1.8-1.7 3.1 3.2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M16.5 20.5H6a2.5 2.5 0 0 1-2.5-2.5V7.5" strokeLinecap="round" />
    </svg>
  );
}

function MicIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <rect x="9" y="3" width="6" height="10.5" rx="3" />
      <path d="M5.5 12a6.5 6.5 0 0 0 13 0M12 18.5V21" strokeLinecap="round" />
    </svg>
  );
}

/**
 * Sync state, stated plainly.
 *
 * A cleaner in a basement needs to know that their photos are safe but not
 * yet sent — not to be told nothing, and not to be shown an error that
 * suggests they were lost. The three states here are exactly the three things
 * that can be true, and the only one with an action attached is the one where
 * an action helps.
 */
function SyncBadge({
  status,
  onRetry,
}: {
  status: {
    pending: number;
    uploaded: number;
    abandoned: number;
    syncing: boolean;
    online: boolean;
    lastError: string | null;
  };
  onRetry: () => void;
}) {
  const { t } = useTranslation();
  const total = status.uploaded + status.pending;

  if (status.abandoned > 0) {
    return (
      <Alert tone="warning" title={t('magicQuote.sync.abandonedTitle')}>
        {t('magicQuote.sync.abandonedBody', { count: status.abandoned })}
      </Alert>
    );
  }
  if (!status.online) {
    return (
      <p
        className="rounded-lg border border-[color:var(--color-warning)]/30 bg-[color:var(--color-warning-soft)] px-3 py-2 text-sm font-medium text-[color:var(--color-warning)]"
        role="status"
      >
        {t('magicQuote.sync.offline', { count: status.pending })}
      </p>
    );
  }
  if (status.pending > 0) {
    // A bar and a fraction, not a bare countdown. "11 still to upload" only
    // ever goes down, so a cleaner watching it has no way to tell nearly-done
    // from barely-started — and this is the number standing between them and
    // the Finish button.
    return (
      <div
        className="rounded-lg border border-[color:var(--color-line)] bg-[color:var(--color-surface)] px-3 py-2 text-sm text-[color:var(--color-ink)]"
        role="status"
        aria-live="polite"
      >
        <div className="flex items-center justify-between gap-3">
          <span>{t('magicQuote.sync.uploadingProgress', { done: status.uploaded, total })}</span>
          {!status.syncing ? (
            <button type="button" className="font-semibold text-[color:var(--color-brand)] underline" onClick={onRetry}>
              {t('magicQuote.sync.retry')}
            </button>
          ) : null}
        </div>
        <div
          className="mt-2 h-2 overflow-hidden rounded-full bg-[color:var(--color-line)]"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={total}
          aria-valuenow={status.uploaded}
        >
          <div
            className="h-full rounded-full bg-[color:var(--color-brand)] transition-[width] duration-500"
            // Never a full-looking bar while anything is left: at one of one
            // sent the bar would otherwise read as finished beside a Finish
            // button that is still disabled.
            style={{ width: `${total > 0 ? Math.min(95, Math.round((status.uploaded / total) * 100)) : 5}%` }}
          />
        </div>
        {/* Why it is not moving, when it is not moving. A count on its own
            reads as a bug the moment it stops going down, and the cleaner
            standing in the customer's kitchen has no way to tell a basement
            from a broken upload. */}
        {status.lastError ? (
          <p className="mt-1.5 text-xs text-[color:var(--color-warning)]">
            {t('magicQuote.sync.stalled', { reason: status.lastError })}
          </p>
        ) : null}
      </div>
    );
  }
  return (
    <p
      className="flex items-center gap-2 rounded-lg border border-[color:var(--color-brand)]/30 bg-[color:var(--color-brand-soft)] px-3 py-2 text-sm font-medium text-[color:var(--color-brand-strong)]"
      role="status"
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        aria-hidden="true"
      >
        <path d="M4 12.5l5 5L20 6.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      {t('magicQuote.sync.synced')}
    </p>
  );
}

function formatClock(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}
